import { useForm } from "@tanstack/react-form";
import { zodValidator } from "@tanstack/zod-form-adapter";
import { purchaseRequestSchema } from "@/lib/schemas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  TanstackFieldWrapper,
  TanstackFormControl,
} from "@/components/ui/tanstack-form";
import { Loader2, Package2 } from "lucide-react";
import { focusFirstError } from "@/lib/form/formUtils";
import { useState } from "react";
import { NumericInput } from "@/components/ui/numeric-input";
import { Badge } from "@/components/ui/badge";
import { SharedCombobox } from "@/components/shared/SharedCombobox";

import { toVnDateString } from "@apv-erp/status";
interface PurchaseRequestFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: any) => Promise<void>;
  initialData?: any;
}

export function PurchaseRequestForm({
  open,
  onOpenChange,
  onSubmit,
  initialData,
}: PurchaseRequestFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasMaterialContext = !!(initialData?.materialId || initialData?.plannedMaterialId || initialData?.id || initialData?.materialPlanId);

  const form = useForm({
    defaultValues: {
      material_id:
        initialData?.plannedMaterialId || initialData?.materialId || "",
      material_plan_id: initialData?.id || initialData?.materialPlanId || "",
      material_name: initialData?.materialName || initialData?.name || "",
      quantity: Math.max(1, Number(initialData?.shortageQty || initialData?.quantity || 1)),
      required_date: initialData?.requiredDate ? toVnDateString(initialData.requiredDate) : toVnDateString(Date.now() + 3 * 24 * 60 * 60 * 1000),
      reason: initialData?.reason || (initialData?.poCode ? `Bổ sung vật tư cho LSX ${initialData.poCode}` : ""),
    },
    // @ts-ignore
    validatorAdapter: zodValidator(),
    validators: { onChange: purchaseRequestSchema as any },
    onSubmit: async ({ value }) => {
      setIsSubmitting(true);
      try {
        await onSubmit(value);
        form.reset();
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Tạo Yêu Cầu Mua Hàng (PR)</DialogTitle>
          <DialogDescription>
            Tạo đề nghị mua vật tư mới cho bộ phận Thu Mua.
          </DialogDescription>
        </DialogHeader>

        {hasMaterialContext && (
          <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 rounded-lg border border-indigo-200 text-xs">
            <Package2 className="w-4 h-4 text-indigo-600 shrink-0" />
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="font-semibold text-indigo-800 truncate">
                {initialData?.materialName || initialData?.name || "Vật tư"}
              </span>
              {initialData?.shortageQty > 0 && (
                <span className="text-indigo-600">
                  Thiếu hụt: <Badge variant="outline" className="text-[10px] border-rose-300 text-rose-700 bg-rose-50 font-bold">{initialData.shortageQty}</Badge>
                </span>
              )}
            </div>
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
            requestAnimationFrame(() => focusFirstError(form.state as any));
          }}
          className="grid grid-cols-[minmax(0,1fr)] gap-4 py-4"
        >
          <form.Field name="material_id">
            {(field: any) => (
              <TanstackFieldWrapper
                field={field}
                label="Vật tư"
                className="grid grid-cols-4 items-center gap-4"
              >
                <TanstackFormControl>
                  <div className="col-span-3">
                    <SharedCombobox
                      type="material"
                      value={field.state.value || ""}
                      valueLabel={form.getFieldValue("material_name") || ""}
                      onChange={(id, opt) => {
                        field.handleChange(id);
                        form.setFieldValue("material_name", opt?.name || opt?.label || "");
                      }}
                      disabled={hasMaterialContext}
                      placeholder="Chọn vật tư cần yêu cầu mua..."
                      hideLabel
                    />
                  </div>
                </TanstackFormControl>
              </TanstackFieldWrapper>
            )}
          </form.Field>
          <form.Field name="quantity">
            {(field: any) => (
              <TanstackFieldWrapper
                field={field}
                label="Số lượng"
                className="grid grid-cols-4 items-center gap-4"
              >
                <TanstackFormControl>
                  <NumericInput
                    placeholder="0"
                    value={field.state.value || ""}
                    onChange={(e) => field.handleChange(Number(e.target.value))}
                    onBlur={field.handleBlur}
                    className="col-span-3"
                  />
                </TanstackFormControl>
              </TanstackFieldWrapper>
            )}
          </form.Field>
          <form.Field name="required_date">
            {(field: any) => (
              <TanstackFieldWrapper
                field={field}
                label="Ngày cần hàng"
                className="grid grid-cols-4 items-center gap-4"
              >
                <TanstackFormControl>
                  <Input
                    type="date"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    className="col-span-3"
                  />
                </TanstackFormControl>
              </TanstackFieldWrapper>
            )}
          </form.Field>
          <form.Field name="reason">
            {(field: any) => (
              <TanstackFieldWrapper
                field={field}
                label="Lý do/Ghi chú"
                className="grid grid-cols-4 items-center gap-4"
              >
                <TanstackFormControl>
                  <Input
                    placeholder="Phục vụ LSX..."
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    className="col-span-3"
                  />
                </TanstackFormControl>
              </TanstackFieldWrapper>
            )}
          </form.Field>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="h-9 text-sm"
              disabled={isSubmitting}
            >
              Hủy
            </Button>
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit, isFormSubmitting]: any) => (
                <Button
                  type="submit"
                  className="h-9 text-sm bg-indigo-600"
                  disabled={!canSubmit || isSubmitting || isFormSubmitting}
                >
                  {isSubmitting ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : null}
                  Gửi yêu cầu
                </Button>
              )}
            />
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
